import React, { useState, useEffect } from "react";
import {
    Grid,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Typography,
    Paper,
    Alert,
    Tooltip,
    FormControlLabel, Checkbox,
} from "@mui/material";
import {
    Business as BusinessIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../../../vlp/components/common/CommonLoader";
import ClientSelectorDialog from "../../../vlp/components/common/ClientSelectorDialog";
import UserSelectorDialog from "../../../vlp/components/common/UserSelectorDialog";
import { CreateETKBulkOrders } from "../../../vlp/services/OrderService";
import { GetEtkKits, SetLastUser } from "../../../services/usersService";
import { getFormattedDateTime } from "../../../services/dateService";
import { appInsights } from "../../../services/appInisights";

export default function CreateBulkOrders() {
    const [loading, setLoading] = useState(false);
    const [etkKits, setEtkKits] = useState([]);

    // Form data
    const [kitType, setKitType] = useState("");
    const [payType, setPayType] = useState("");
    const [testKitCount, setTestKitCount] = useState(1);
    const [account, setAccount] = useState("");
    const [accountValid, setAccountValid] = useState(false);

    // User and Client States
    const [currentUser, setCurrentUser] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [userSelectorOpen, setUserSelectorOpen] = useState(false);

    // Client Dialog States
    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [errors, setErrors] = useState({});

    const [needZplFile, setNeedZplFile] = useState(false);
    const [needQrImages, setNeedQrImages] = useState(false);
    const [needQrSvg, setNeedQrSvg] = useState(false);


    // Load ETK kits on mount
    useEffect(() => {
        async function loadData() {
            setLoading(true);
            try {
                const kits = await GetEtkKits();
                setEtkKits(kits);
            } catch (err) {
                NotificationManager.error("Error getting ETK kits from the API");
                appInsights.trackException({ exception: err });
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, []);

    // Handle user selection
    const handleUserSelect = (user) => {
        if (user) {
            setSelectedUser(user);
            setCurrentUser(user.operator);
            SetLastUser(user.operator);
            setErrors((prev) => ({ ...prev, user: "" }));
        }
    };

    // Handle client selection
    const handleClientSelect = (client) => {
        if (client) {
            setSelectedClient(client);
            setAccount(client.accountNumber);
            setAccountValid(true);
            setErrors((prev) => ({ ...prev, accountNumber: "" }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        if (!selectedUser) {
            newErrors.user = "User is required";
            isValid = false;
        }

        if (!account) {
            newErrors.accountNumber = "Account is required";
            isValid = false;
        }

        if (!kitType) {
            newErrors.kitType = "Kit Type is required";
            isValid = false;
        }

        if (!payType) {
            newErrors.payType = "Pay Type is required";
            isValid = false;
        }

        if (!testKitCount || testKitCount < 1) {
            newErrors.testKitCount = "Count must be at least 1";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            NotificationManager.error("Please fill in all required fields");
            return;
        }

        try {
            setLoading(true);

            // Prepare data for API - single order object
            const bulkOrderData = {
                accountNumber: account,
                orderType: "ETK",
                selectedKit: kitType,
                payType: payType,
                kitCount: testKitCount,
                user: currentUser.trim(),
                needZplFile: needZplFile,
                needQrImages: needQrImages,
                needQrSvg : needQrSvg,
            };


            // Call the API - it will automatically download the file
            await CreateETKBulkOrders(bulkOrderData);

            NotificationManager.success("Successfully created bulk ETK orders and downloaded Excel file");

            // Reset form
            handleReset();

        } catch (error) {
            appInsights.trackEvent({
                name: "Error Creating Bulk ETK Orders",
                properties: {
                    message: "Error creating bulk ETK orders from CreateBulkOrders.js",
                    error: error,
                },
            });
            appInsights.trackException({ exception: error });
            NotificationManager.error(
                "Unable to create bulk orders. Error: " + error,
                "Error Submitting"
            );
        } finally {
            setLoading(false);
        }
    };

    // Reset form
    const handleReset = () => {
        setKitType("");
        setPayType("");
        setTestKitCount(1);
        setAccount("");
        setAccountValid(false);
        setSelectedClient(null);
        setSelectedUser(null);
        setCurrentUser("");
        setErrors({});
        setNeedZplFile(false);
        setNeedQrImages(false);
        setNeedQrSvg(false);

    };

    // Check if kit count is large (>100)
    const isLargeOrder = testKitCount > 100;

    return (
        <>
            <div className="vlp-component-container" style={{ marginRight: "15px" }}>
                <Box sx={{ marginBottom: 3 }}>
                    <Typography variant="h5" className="title" sx={{ fontWeight: 600, mb: 1 }}>
                        Create Bulk Orders
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Generate multiple ETK orders at once and download as Excel file
                    </Typography>
                </Box>

                <hr style={{ marginBottom: "24px" }} />

                {/* Main Form Card */}
                <Paper elevation={2} sx={{ padding: 3, marginBottom: 3, borderRadius: 2 }}>
                    <Grid container spacing={3}>
                        {/* User Field */}
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="User *"
                                value={
                                    selectedUser
                                        ? `${selectedUser.operator.trim()} - ${selectedUser.aliasName.trim()}`
                                        : ""
                                }
                                placeholder="Click to select user"
                                size="small"
                                fullWidth
                                onClick={() => setUserSelectorOpen(true)}
                                InputProps={{
                                    readOnly: true,
                                    style: { cursor: "pointer" },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color={selectedUser ? "success" : "primary"} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: selectedUser ? (
                                        <InputAdornment position="end">
                                            <Tooltip title="Change user">
                                                <EditIcon color="primary" sx={{ fontSize: 20 }} />
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                helperText={
                                    errors.user ||
                                    (selectedUser ? "Click to change user" : "Click to select user")
                                }
                                error={!!errors.user}
                            />
                        </Grid>

                        {/* Account Number */}
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                label="Account Number *"
                                value={
                                    selectedClient
                                        ? `${account.trim()} - ${selectedClient.clientName.trim()}`
                                        : ""
                                }
                                placeholder="Click to select client"
                                size="small"
                                fullWidth
                                onClick={() => setClientDialogOpen(true)}
                                InputProps={{
                                    readOnly: true,
                                    style: { cursor: "pointer" },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <BusinessIcon color={selectedClient ? "success" : "primary"} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: selectedClient ? (
                                        <InputAdornment position="end">
                                            <Tooltip title="Change client">
                                                <EditIcon color="primary" sx={{ fontSize: 20 }} />
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                helperText={
                                    errors.accountNumber ||
                                    (selectedClient
                                        ? "Click to change client"
                                        : "Click to select a client")
                                }
                                error={!!errors.accountNumber}
                            />
                        </Grid>

                        {/* Kit Type */}
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                                error={!!errors.kitType}
                            >
                                <InputLabel>Kit Type *</InputLabel>
                                <Select
                                    label="Kit Type *"
                                    value={kitType}
                                    onChange={(e) => {
                                        setKitType(e.target.value);
                                        setErrors((prev) => ({ ...prev, kitType: "" }));
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select Kit Type</em>
                                    </MenuItem>
                                    {etkKits.map((kit, idx) => {
                                        const capitalizedOption =
                                            kit.vlpName.charAt(0).toUpperCase() +
                                            kit.vlpName.slice(1).toLowerCase();
                                        return (
                                            <MenuItem key={idx} value={kit.vlpName}>
                                                {capitalizedOption}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                                {errors.kitType && (
                                    <span style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px", marginLeft: "14px" }}>
                                        {errors.kitType}
                                    </span>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Pay Type */}
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl
                                fullWidth
                                size="small"
                                error={!!errors.payType}
                            >
                                <InputLabel>Pay Type *</InputLabel>
                                <Select
                                    label="Pay Type *"
                                    value={payType}
                                    onChange={(e) => {
                                        setPayType(e.target.value);
                                        setErrors((prev) => ({ ...prev, payType: "" }));
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select Pay Type</em>
                                    </MenuItem>
                                    <MenuItem value="pre-pay">At Purchase (Pre-pay)</MenuItem>
                                    <MenuItem value="post-pay">After Purchase (Post-pay)</MenuItem>
                                </Select>
                                {errors.payType && (
                                    <span style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "3px", marginLeft: "14px" }}>
                                        {errors.payType}
                                    </span>
                                )}
                            </FormControl>
                        </Grid>

                        {/* Test Kit Count */}
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                size="small"
                                label="Test Kit Count *"
                                type="number"
                                fullWidth
                                value={testKitCount}
                                onChange={(e) => {
                                    setTestKitCount(parseInt(e.target.value) || 1);
                                    setErrors((prev) => ({ ...prev, testKitCount: "" }));
                                }}
                                inputProps={{ min: 1, max: 10000 }}
                                helperText={errors.testKitCount || "Number of kits to create (1-10,000)"}
                                error={!!errors.testKitCount}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title="Enter the number of test kits you want to create">
                                                <InfoIcon fontSize="small" color="primary" />
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        {/* ZPL & QR Options */}
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={needZplFile}
                                        onChange={(e) => setNeedZplFile(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Do you need ZPL Labels file?"
                            />

                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={needQrImages}
                                        onChange={(e) => setNeedQrImages(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Do you need QR Code Images?"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={needQrSvg}
                                        onChange={(e) => setNeedQrSvg(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Do you need QR Code SVGs?"
                            />
                        </Grid>


                    </Grid>
                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>

                        <Button
                            variant="outlined"
                            color="error"
                            size="large"
                            onClick={() => (window.location.href = "/vlp/new-order")}
                        >
                            Cancel
                        </Button>


                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleReset}
                            size="large"
                            disabled={loading}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleSubmit}
                            size="large"
                            disabled={loading}
                            sx={{ minWidth: 120 }}
                        >
                            {loading ? "Processing..." : "Submit"}
                        </Button>
                    </Box>
                </Paper>

                {/* Warning for large orders */}
                {isLargeOrder && (
                    <Alert
                        severity="warning"
                        icon={<WarningIcon />}
                        sx={{ marginBottom: 3 }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Large Order Detected ({testKitCount} kits)
                        </Typography>
                        <Typography variant="body2">
                            This order will consume significant system resources. Consider running it during off-peak hours when fewer users are expected to be active.
                        </Typography>
                    </Alert>
                )}

                {/* Performance Notice */}
                <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ mt: 3, mb: 3, p: 2, borderLeft: "6px solid #0288d1", marginBottom: 3 }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ⚡ Performance Notice
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Creating bulk orders is a <strong>memory-intensive process</strong>. For large orders (500+ kits),
                        please schedule this task during <strong>off-peak hours</strong> (evenings or weekends) to avoid
                        impacting other users. Processing time increases with order size.
                    </Typography>

                    <Typography variant="body2">
                        All generated <strong>QR images</strong> and <strong>QR code text files</strong> are automatically
                        saved to the shared drive at:
                        <br />
                        <strong>\\ENVLAB-FILE01\vdrive\BulkQRLabelImages</strong>
                        <br />
                        You can open that folder and print the QR images if needed.
                    </Typography>
                </Alert>

                <Alert
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ mt: 3, mb: 3, p: 2, borderLeft: "6px solid #0288d1" }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        ⚡ QR Printing Notice
                    </Typography>

                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        Your old bulk order QR codes are ready to print
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                        The system has generated barcode/QR images and ZPL files on the shared drive.
                        If you want to print these QR codes now, click the button below.
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        size="medium"
                        onClick={() => (window.location.href = "/vlp/print-bulk-order")}
                    >
                        Go to Print QR Codes
                    </Button>
                </Alert>




                <CommonLoader loading={loading} />
            </div>

            {/* Client Selector Dialog */}
            <ClientSelectorDialog
                open={clientDialogOpen}
                onClose={() => setClientDialogOpen(false)}
                onSelect={handleClientSelect}
                multiSelect={false}
            />

            {/* User Selector Dialog */}
            <UserSelectorDialog
                open={userSelectorOpen}
                onClose={() => setUserSelectorOpen(false)}
                onSelect={handleUserSelect}
                multiSelect={false}
                filter="SHIPPING"
            />
        </>
    );
}